import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import Popup from "reactjs-popup";
import { Button, Plus, Trash01 } from '@opendate-app/setlist';

const popupStyle = {
  "background": "var(--sl-bg-primary)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "480px",
  "padding": "0px"
};

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every Two Weeks' },
  { value: 'every_other_day', label: 'Every Other Day' },
  { value: 'specific_days', label: 'Specific Days' }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour = i;
  const ampm = hour >= 12 ? 'pm' : 'am';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return { value: hour, label: `${displayHour}:00 ${ampm}` };
});

const TicketUpdatePreferencesModal = ({
  open,
  onClose,
  calendarEventContact,
  onSave,
  saving
}) => {
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [selectedDays, setSelectedDays] = useState([]);
  const nextRowKey = useRef(0);
  const buildRow = (hour) => ({ key: nextRowKey.current++, hour });
  const [hourRows, setHourRows] = useState(() => [buildRow(6)]);

  useEffect(() => {
    if (open && calendarEventContact) {
      const savedHours = calendarEventContact.ticket_update_hours
        ?? [calendarEventContact.ticket_update_hour].filter(hour => hour != null);
      setEnabled(calendarEventContact.send_daily_ticket_updates || false);
      setFrequency(calendarEventContact.ticket_update_frequency || 'daily');
      setSelectedDays(calendarEventContact.ticket_update_days || []);
      setHourRows((savedHours?.length ? savedHours : [6]).map(buildRow));
    }
  }, [open, calendarEventContact]);

  const handleClose = () => {
    onClose();
  };

  const isDaySelectionValid = () => {
    if (frequency === 'weekly' || frequency === 'biweekly') {
      return selectedDays.length === 1;
    }
    if (frequency === 'specific_days') {
      return selectedDays.length > 0;
    }
    return true;
  };

  const isValid = () => {
    if (!enabled) return true;

    return isDaySelectionValid();
  };

  const getValidationMessage = () => (
    frequency === 'specific_days' ? 'Please select at least one day' : 'Please select a day of the week'
  );

  const handleSave = () => {
    if (!isValid()) return;

    const params = {
      send_daily_ticket_updates: enabled,
      ticket_update_frequency: frequency,
      ticket_update_days: selectedDays,
      ticket_update_hours: [...new Set(hourRows.map(row => row.hour))].sort((a, b) => a - b)
    };
    onSave(params);
  };

  const chosenHours = hourRows.map(row => row.hour);

  const addHourRow = () => {
    const lastHour = chosenHours[chosenHours.length - 1];
    const nextHour = Array.from({ length: 24 }, (_, offset) => (lastHour + 1 + offset) % 24)
      .find(hour => !chosenHours.includes(hour));
    setHourRows(prev => [...prev, buildRow(nextHour)]);
  };

  const changeHourRow = (key, hour) => {
    setHourRows(prev => prev.map(row => (row.key === key ? { ...row, hour } : row)));
  };

  const removeHourRow = (key) => {
    setHourRows(prev => prev.filter(row => row.key !== key));
  };

  const handleDayToggle = (dayValue) => {
    if (frequency === 'weekly' || frequency === 'biweekly') {
      setSelectedDays([dayValue]);
    } else {
      setSelectedDays(prev =>
        prev.includes(dayValue)
          ? prev.filter(d => d !== dayValue)
          : [...prev, dayValue].sort((a, b) => a - b)
      );
    }
  };

  const showDaySelector = ['weekly', 'biweekly', 'specific_days'].includes(frequency);
  const isMultiSelect = frequency === 'specific_days';
  const venueTimeZone = calendarEventContact?.venue_time_zone;

  return (
    <Popup
      open={open}
      contentStyle={popupStyle}
      closeOnDocumentClick={!saving}
      className="popup-modal"
      onClose={handleClose}
    >
      <div className="popup-modal-container">
        <div className="row m-0" style={{ borderBottom: "1px solid var(--sl-border-secondary)" }}>
          <div className="col d-flex" style={{ padding: "17px 24px" }}>
            <h3 className="mb-0 align-self-center" style={{ fontSize: "20px" }}>
              <strong>
                <i className="far fa-envelope text-primary mr-2"></i>
                Ticket Count Email Settings
              </strong>
            </h3>
          </div>
          <div className="col-xs-auto d-flex align-items-center" style={{ paddingRight: "24px" }}>
            <a
              href="#"
              style={{ fontSize: "20px" }}
              className="text-dark"
              onClick={(e) => {
                e.preventDefault();
                if (!saving) handleClose();
              }}
            >
              <i className="fal fa-times"></i>
            </a>
          </div>
        </div>

        <div className="row m-0">
          <div className="col-12" style={{ padding: "18px 24px 24px 24px" }}>
            <div className="mb-3">
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="ticket-updates-enabled"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  disabled={saving}
                />
                <label className="custom-control-label" htmlFor="ticket-updates-enabled">
                  <strong>Enable ticket count emails</strong>
                </label>
              </div>
              <small className="text-muted d-block mt-1">
                Receive emails with ticket sales information for this event
              </small>
            </div>

            {enabled && (
              <>
                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '14px' }}>
                    <strong>Frequency</strong>
                  </label>
                  <select
                    className="custom-select"
                    value={frequency}
                    onChange={(e) => {
                      setFrequency(e.target.value);
                      if (e.target.value === 'daily' || e.target.value === 'every_other_day') {
                        setSelectedDays([]);
                      }
                    }}
                    disabled={saving}
                  >
                    {FREQUENCIES.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {showDaySelector && (
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: '14px' }}>
                      <strong>{isMultiSelect ? 'Select days' : 'Select day'}</strong>
                    </label>
                    <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          className={`btn btn-sm ${selectedDays.includes(day.value) ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ borderRadius: '20px', minWidth: '50px' }}
                          onClick={() => handleDayToggle(day.value)}
                          disabled={saving}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                    {!isDaySelectionValid() ? (
                      <small className="text-danger d-block mt-1">
                        {getValidationMessage()}
                      </small>
                    ) : !isMultiSelect ? (
                      <small className="text-muted d-block mt-1">
                        Select the day of the week to receive emails
                      </small>
                    ) : (
                      <small className="text-muted d-block mt-1">
                        Select one or more days to receive emails
                      </small>
                    )}
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label" style={{ fontSize: '14px' }}>
                    <strong>Send time</strong>
                  </label>
                  {hourRows.map((row, index) => (
                    <div key={row.key} className={`d-flex align-items-center${index > 0 ? ' mt-2' : ''}`}>
                      <select
                        className="custom-select"
                        aria-label={`Send time ${index + 1}`}
                        value={row.hour}
                        onChange={(e) => changeHourRow(row.key, parseInt(e.target.value, 10))}
                        disabled={saving}
                      >
                        {HOURS.map(h => (
                          <option
                            key={h.value}
                            value={h.value}
                            disabled={h.value !== row.hour && chosenHours.includes(h.value)}
                          >
                            {h.label}
                          </option>
                        ))}
                      </select>
                      {hourRows.length > 1 && (
                        <Button
                          variant="tertiary"
                          size="sm"
                          icon={Trash01}
                          className="ml-2 flex-shrink-0"
                          aria-label={`Remove ${HOURS[row.hour].label}`}
                          onClick={() => removeHourRow(row.key)}
                          disabled={saving}
                        />
                      )}
                    </div>
                  ))}
                  {hourRows.length < HOURS.length && (
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon={Plus}
                      className="mt-2"
                      onClick={addHourRow}
                      disabled={saving}
                    >
                      Add another time
                    </Button>
                  )}
                  {venueTimeZone && (
                    <small className="text-muted d-block mt-1">
                      Emails sent in {venueTimeZone} (venue timezone)
                    </small>
                  )}
                </div>
              </>
            )}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                style={{ borderRadius: "8px", fontSize: "14px" }}
                className="btn btn-outline-secondary"
                onClick={handleClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ borderRadius: "8px", fontSize: "14px" }}
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving || !isValid()}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Saving...
                  </>
                ) : (
                  <strong>Save</strong>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

TicketUpdatePreferencesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  calendarEventContact: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool
};

TicketUpdatePreferencesModal.defaultProps = {
  saving: false
};

export default TicketUpdatePreferencesModal;
